import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  Badge,
  Center,
  Container,
  Group,
  Loader,
  Select,
  Stack,
  Switch,
  Table,
  Text,
  Tooltip,
} from '@mantine/core';

import { ContractMeter, LogoPaper, meterState, PageTitle } from '@/components';
import { todayIso } from '@/components/ProviderCosts/format';
import { useClientContracts, useTeams } from '@/hooks';
import { contractPeriod, type ContractSummary } from '@/types/client-contract';
import { formatMoney } from '@/utils';

const PERIOD_BADGE = {
  current: { label: 'Current', color: 'green' },
  upcoming: { label: 'Upcoming', color: 'blue' },
  ended: { label: 'Ended', color: 'gray' },
} as const;

function percentText(rate: number): string {
  return `${(rate * 100).toLocaleString('en-GB', { maximumFractionDigits: 2 })}%`;
}

/** What needs a second look about a contract's figures, as short badges. */
function Flags({ contract }: Readonly<{ contract: ContractSummary }>) {
  const otherCurrencies = Object.keys(contract.otherCurrencyTotals);
  return (
    <Group gap={4}>
      {contract.currency === null ? (
        <Tooltip label="Backoffice holds no currency for this contract, so nothing can draw down">
          <Badge size="sm" color="red" variant="light">
            No currency
          </Badge>
        </Tooltip>
      ) : null}
      {contract.overlappingContractIds.length > 0 ? (
        <Tooltip label="Another contract of this team covers some of the same days; Backoffice picks one arbitrarily">
          <Badge size="sm" color="orange" variant="light">
            Overlaps
          </Badge>
        </Tooltip>
      ) : null}
      {otherCurrencies.length > 0 ? (
        <Tooltip
          label={`Work billed in ${otherCurrencies.join(', ')} does not draw down this ${contract.currency ?? ''} contract`}
        >
          <Badge size="sm" color="gray" variant="light">
            Other currency
          </Badge>
        </Tooltip>
      ) : null}
      {contract.exceptionalCircumstances ? (
        <Tooltip label="Discounted throughout, even past the commitment">
          <Badge size="sm" color="blue" variant="light">
            Exceptional
          </Badge>
        </Tooltip>
      ) : null}
    </Group>
  );
}

/**
 * Admin: every client contract with what has been drawn against it. Figures
 * are computed from the consumption ledger, which refreshes every half hour.
 */
export function UsageContractsPage() {
  const navigate = useNavigate();
  const { teams } = useTeams();
  const [teamId, setTeamId] = useState<string | null>(null);
  const [activeOnly, setActiveOnly] = useState(true);
  const today = todayIso();
  const { contracts, isLoading, error } = useClientContracts({
    teamId: teamId ?? undefined,
    activeOn: activeOnly ? today : undefined,
  });

  const teamOptions = useMemo(
    () =>
      teams
        .map((team) => ({ value: String(team.id), label: team.name }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [teams]
  );

  return (
    <Container size="xl" py="md">
      <Stack>
        <Stack gap={4}>
          <PageTitle title="Usage" />
          <Text size="sm" c="dimmed" maw={760}>
            Each client contract against what has been drawn from it. Only billable work in the
            contract&apos;s currency draws down; the rest is listed on the contract without
            counting. Contracts come from Backoffice and the figures refresh every half hour.
          </Text>
        </Stack>

        <Group>
          <Select
            placeholder="All teams"
            aria-label="Team"
            clearable
            searchable
            data={teamOptions}
            value={teamId}
            onChange={setTeamId}
            w={260}
          />
          <Switch
            label="Current contracts only"
            checked={activeOnly}
            onChange={(e) => setActiveOnly(e.currentTarget.checked)}
          />
        </Group>

        {error ? <Text c="red">Could not load contracts: {error.message}</Text> : null}

        <LogoPaper>
          {isLoading && contracts.length === 0 ? (
            <Center h={160}>
              <Loader />
            </Center>
          ) : contracts.length === 0 ? (
            <Text size="sm" c="dimmed" ta="center" py="lg">
              No contracts match.
            </Text>
          ) : (
            <Table highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Client</Table.Th>
                  <Table.Th>Period</Table.Th>
                  <Table.Th ta="right">Commitment</Table.Th>
                  <Table.Th w={220}>Drawn</Table.Th>
                  <Table.Th ta="right">Remaining</Table.Th>
                  <Table.Th ta="right">Discount</Table.Th>
                  <Table.Th />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {contracts.map((contract) => {
                  const period = PERIOD_BADGE[contractPeriod(contract, today)];
                  const over = meterState(contract.consumed, contract.commitmentAmount) === 'over';
                  return (
                    <Table.Tr
                      key={contract.id}
                      style={{ cursor: 'pointer' }}
                      onClick={() => navigate(`/admin/usage/${contract.id}`)}
                      data-testid={`contract-row-${contract.id}`}
                    >
                      <Table.Td>
                        <Text fw={500}>{contract.clientName ?? `Team ${contract.teamId}`}</Text>
                        <Text size="xs" c="dimmed">
                          Contract {contract.backofficeContractId}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Group gap="xs" wrap="nowrap">
                          <Text size="sm">
                            {contract.startsOn} to {contract.endsOn}
                          </Text>
                          <Badge size="sm" variant="light" color={period.color}>
                            {period.label}
                          </Badge>
                        </Group>
                      </Table.Td>
                      <Table.Td ta="right">
                        {formatMoney(contract.commitmentAmount, contract.currency)}
                      </Table.Td>
                      <Table.Td>
                        <Stack gap={2}>
                          <ContractMeter
                            commitment={contract.commitmentAmount}
                            consumed={contract.consumed}
                            currency={contract.currency}
                            compact
                          />
                          <Text size="xs" c="dimmed">
                            {formatMoney(contract.consumed, contract.currency)}
                          </Text>
                        </Stack>
                      </Table.Td>
                      <Table.Td ta="right">
                        {over ? (
                          <Text size="sm" fw={600}>
                            Over by {formatMoney(contract.overage, contract.currency)}
                          </Text>
                        ) : (
                          formatMoney(contract.remaining, contract.currency)
                        )}
                      </Table.Td>
                      <Table.Td ta="right">{percentText(contract.discountRate)}</Table.Td>
                      <Table.Td>
                        <Flags contract={contract} />
                      </Table.Td>
                    </Table.Tr>
                  );
                })}
              </Table.Tbody>
            </Table>
          )}
        </LogoPaper>
      </Stack>
    </Container>
  );
}
